import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, Box, Typography, CircularProgress, Button } from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { verifySession } from '../services/subscriptionService';
import { trackLinkedInCheckoutSuccess } from '../services/linkedInInsightTag';
import { trackXCheckoutSuccess } from '../services/xAdsPixel';
import { trackMetaPurchase } from '../services/metaPixel';
import { colors } from '../styles';
import { trackRedditPurchase } from '../services/redditPixel';

const SubscriptionVerificationModal = ({ 
  open, 
  onClose, 
  sessionId,
  onVerificationComplete 
}) => {
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const checkSession = async () => {
      if (!sessionId) return;

      setStatus('verifying');
      setErrorMessage('');

      // Add initial delay to allow Stripe to process
      await new Promise(resolve => setTimeout(resolve, 2000));

      const maxRetries = 5;
      const baseDelay = 2000;

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          const result = await verifySession(sessionId);
          
          if (result.success) {
            trackRedditPurchase({
              amountTotalCents: result.amountTotal,
              currency: result.currency,
              localDedupeKey: sessionId,
            });
            setStatus('success');
            trackLinkedInCheckoutSuccess({ localDedupeKey: sessionId });
            try {
              trackXCheckoutSuccess({
                amountTotalCents: result.amountTotal,
                currency: result.currency,
                localDedupeKey: sessionId,
              });
            } catch (_) {
              // Ad measurement never turns a paid checkout into an error.
            }
            try {
              trackMetaPurchase({
                amountTotalCents: result.amountTotal,
                currency: result.currency,
                invoiceId: result.invoiceId,
                sessionId,
              });
            } catch (_) {
              // Ad measurement never turns a paid checkout into an error.
            }
            // Wait a moment to show the success state, then complete
            await new Promise(resolve => setTimeout(resolve, 2000));
            onVerificationComplete(result);
            onClose();
            return;
          }

          if (result.status === 'processing') {
            // If processing, always wait and retry
            const delay = baseDelay * Math.pow(2, attempt);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }

          // If we get here, it's a non-processing failure
          throw new Error(result.error || 'Verification failed');
          
        } catch (err) {
          if (attempt === maxRetries - 1) {
            setStatus('error');
            setErrorMessage(err.message || 'Failed to verify subscription');
            return;
          }

          // Exponential backoff for other errors
          const delay = baseDelay * Math.pow(2, attempt);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    };

    if (open && sessionId) {
      checkSession();
    } else if (open && !sessionId) {
      // Reset state when modal opens without sessionId
      setStatus('verifying');
      setErrorMessage('');
    }
  }, [open, sessionId, onClose, onVerificationComplete]);

  const handleClose = () => {
    setStatus('verifying');
    setErrorMessage('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      maxWidth="xs" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          maxWidth: '400px !important',
          backgroundColor: colors.white,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
          overflow: 'visible'
        }
      }}
    >
      <DialogContent sx={{ p: 4, textAlign: 'center' }}>
        {status === 'verifying' && (
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            py={2}
            gap={2}
          >
            <CircularProgress size={48} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.secondary }}>
              Verifying your subscription...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This may take a few moments
            </Typography>
          </Box>
        )}

        {status === 'success' && (
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            py={2}
            gap={2}
          >
            <CheckCircleIcon 
              sx={{ 
                fontSize: 80, 
                color: colors.status.ready,
                '@keyframes scaleIn': {
                  '0%': {
                    transform: 'scale(0)',
                    opacity: 0
                  },
                  '50%': {
                    transform: 'scale(1.1)',
                  },
                  '100%': {
                    transform: 'scale(1)',
                    opacity: 1
                  }
                },
                animation: 'scaleIn 0.5s ease-out'
              }} 
            />
            <Typography variant="h5" sx={{ fontWeight: 700, color: colors.secondary }}>
              Subscription Active!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your trial has been successfully activated. You now have access to all premium features.
            </Typography>
          </Box>
        )}

        {status === 'error' && (
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            py={2}
            gap={2}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>
              Verification Failed
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {errorMessage || 'Unable to verify your subscription. Please try again or contact support.'}
            </Typography>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                mt: 2,
                borderRadius: '50px',
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                py: 1
              }}
            >
              Close
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionVerificationModal; 